import { Env } from '@env';
import { usePathname } from 'expo-router';
import { useCallback } from 'react';
import { Platform } from 'react-native';

import { useCustomer } from '@/api/customers/use-customer';
import { useSendMetaEvent } from '@/api/meta/use-send-meta-event';
import metaPixel from '@/core/clients/meta';

export type ConversionType = 'subscription' | 'daily';

type TrackMetaOptions = {
  type?: ConversionType;
  eventId?: string;
  customData?: Record<string, unknown>;
};

function generateEventId(): string {
  if (typeof globalThis !== 'undefined' && globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

// Native has no window.location, but its CAPI events are still labeled
// action_source 'website' (we don't send app_data yet) and Meta requires
// event_source_url on those. Native and web share the same expo-router routes,
// so the canonical web URL for the screen is the closest honest stand-in.
function buildNativeEventSourceUrl(pathname: string): string {
  const origin = Env.BASE_URL.replace(/\/+$/, '');
  const path = pathname.startsWith('/') ? pathname : `/${pathname}`;

  return `${origin}${path}`;
}

export function useTrackMetaEvent() {
  const { mutate: sendMetaEvent } = useSendMetaEvent();
  const { data: customer } = useCustomer();
  const pathname = usePathname();

  return useCallback(
    (eventName: string, options: TrackMetaOptions = {}) => {
      const isWeb = Platform.OS === 'web';
      const eventId = options.eventId ?? generateEventId();
      const webUrl =
        isWeb && typeof window !== 'undefined'
          ? window.location?.href
          : undefined;
      const eventSourceUrl = webUrl ?? buildNativeEventSourceUrl(pathname);

      const customData: Record<string, unknown> = {
        ...options.customData,
        platform: isWeb ? 'web' : 'app',
      };
      if (options.type) customData.type = options.type;

      if (isWeb) {
        if (customer) {
          metaPixel.setUserData({
            email: customer.email,
            phone: customer.phone,
            firstname: customer.firstname,
            lastname: customer.lastname,
            externalId: customer.publicToken,
          });
        }
        metaPixel.track(eventName, customData, eventId);
      }

      // Defer only the CAPI post: waiting on _fbp lets the server event carry
      // the same browser id as the Pixel, which Meta needs to match and dedupe.
      metaPixel.waitForMatchKeys().then(({ fbp, fbc }) => {
        sendMetaEvent({
          eventName,
          eventId,
          actionSource: isWeb ? 'website' : 'app',
          eventSourceUrl,
          fbp,
          fbc,
          customData,
        });
      });
    },
    [sendMetaEvent, customer, pathname]
  );
}
